<template>
  <div>
    <base-header type="gradient-success" />
    <div class="container-fluid mt--6">
      <!-- Header card with report info -->
      <card shadow type="secondary" class="mb-4" noBody>
        <template v-slot:header>
          <div class="bg-white border-0">
            <div class="row align-items-center mb-3">
              <div class="col">
                <router-link to="/tip-splits" class="text-sm text-muted">
                  <i class="ni ni-bold-left mr-1"></i>Back to all tip splits
                </router-link>
              </div>
              <div class="col-auto">
                <button
                  v-if="report && report.status === 'complete'"
                  class="btn btn-sm btn-info"
                  @click="downloadPayrollFiles"
                  :disabled="isDownloading"
                >
                  <i
                    :class="
                      isDownloading
                        ? 'fas fa-spinner fa-spin'
                        : 'fas fa-download'
                    "
                    class="mr-1"
                  ></i>
                  {{ isDownloading ? "Downloading..." : "Download Reports" }}
                </button>
              </div>
            </div>
            <div class="row align-items-start">
              <div class="col">
                <h3 class="mb-0" v-if="report">{{ reportDates }}</h3>
                <div v-if="report" class="text-muted">
                  Status: <span class="text-success">{{ report.status }}</span>
                </div>
                <div v-if="report" class="text-muted text-sm">
                  Run by {{ report.source?.name }} on {{ reportCreatedDate }}
                </div>
                <small class="text-muted">
                  Report ID:
                  <copy-text :text="reportId" label="Report ID" inline>
                    {{ reportId }}
                  </copy-text>
                </small>
              </div>
              <div class="col-auto" v-if="report">
                <span class="badge badge-primary mr-2">
                  {{ report.numberOfTips || 0 }} tips
                </span>
                <span class="badge badge-info">
                  {{ report.locationsWithTips || 0 }} locations
                </span>
              </div>
            </div>
          </div>
        </template>
      </card>

      <!-- Loading state -->
      <card v-if="isLoading" shadow class="text-center py-5">
        <i class="fas fa-spinner fa-spin fa-2x text-primary"></i>
        <p class="mt-3 mb-0">{{ loadingStatus || "Loading..." }}</p>
      </card>

      <!-- Error state -->
      <card v-else-if="error" shadow>
        <div class="alert alert-danger mb-0">
          {{ error }}
        </div>
      </card>

      <!-- Legacy Report state -->
      <card v-else-if="isLegacyReport" shadow>
        <div class="text-center py-4">
          <p class="text-muted mb-0">
            This report was created before the detailed breakdown feature was
            available.<br />
            Breakdown and timesheet data is not available for this report.
          </p>
        </div>
      </card>

      <!-- Main Content with Tabs -->
      <div v-else>
        <!-- Tabs Navigation -->
        <ul class="nav nav-tabs card-tabs">
          <li class="nav-item">
            <a
              class="nav-link"
              :class="{ active: activeTab === 'employees' }"
              href="#"
              @click.prevent="activeTab = 'employees'"
            >
              Split by Employee
            </a>
          </li>
          <li class="nav-item">
            <a
              class="nav-link"
              :class="{ active: activeTab === 'charges' }"
              href="#"
              @click.prevent="activeTab = 'charges'"
            >
              Split by Tip
            </a>
          </li>
          <li class="nav-item">
            <a
              class="nav-link"
              :class="{ active: activeTab === 'timesheets' }"
              href="#"
              @click.prevent="activeTab = 'timesheets'"
            >
              Timesheets
            </a>
          </li>
        </ul>

        <!-- Tab Content -->
        <div v-show="activeTab === 'employees'">
          <employee-tab
            :employees="employeeSummary"
            :report-id="reportId"
            @select="openEmployeeModal"
          />
        </div>

        <div v-show="activeTab === 'charges'">
          <charges-tab
            :charges="breakdownData"
            :report-id="reportId"
            @select="openChargeModal"
          />
        </div>

        <div v-show="activeTab === 'timesheets'">
          <timesheets-tab
            :timesheets="timesheetsData"
            :staff-lookup="staffLookup"
            :location-lookup="locationLookup"
            :report-id="reportId"
          />
        </div>
      </div>

      <!-- Modals -->
      <employee-breakdown-modal
        v-model:show="employeeModal.show"
        :employee="employeeModal.employee"
        @view-tip="viewTipSplit"
      />

      <tip-details-modal
        v-model:show="chargeModal.show"
        :charge="chargeModal.charge"
      />
    </div>
  </div>
</template>

<script>
import moment from "moment";
import CopyText from "@/components/CopyText";
import {
  EmployeeTab,
  ChargesTab,
  TimesheetsTab,
  EmployeeBreakdownModal,
  TipDetailsModal,
} from "@/components/TipSplitDetail";

export default {
  name: "TipSplitDetail",
  components: {
    CopyText,
    EmployeeTab,
    ChargesTab,
    TimesheetsTab,
    EmployeeBreakdownModal,
    TipDetailsModal,
  },
  data() {
    return {
      // Data
      report: null,
      breakdownData: [],
      timesheetsData: [],

      // Lookup Maps
      locationMap: new Map(),

      // UI State
      isLoading: true,
      loadingStatus: "",
      isDownloading: false,
      error: null,
      isLegacyReport: false,
      activeTab: "employees",

      // Modals
      employeeModal: {
        show: false,
        employee: null,
      },
      chargeModal: {
        show: false,
        charge: null,
      },
    };
  },
  computed: {
    reportId() {
      return this.$route.params.reportId;
    },
    reportDates() {
      if (!this.report) return "";
      return `${this.report.startTime} - ${this.report.endTime}`;
    },
    reportCreatedDate() {
      if (!this.report) return "";
      return moment(+this.report.reportId).format("MMM Do YYYY, h:mm a");
    },
    staffLookup() {
      const lookup = {};
      this.breakdownData.forEach((charge) => {
        if (!charge.recipients) return;
        charge.recipients.forEach((r) => {
          if (r.staffId && r.name && !lookup[r.staffId]) {
            lookup[r.staffId] = r.name;
          }
        });
      });
      return lookup;
    },
    locationLookup() {
      const lookup = {};
      this.breakdownData.forEach((charge) => {
        if (charge.locationId && charge.locationName && !lookup[charge.locationId]) {
          lookup[charge.locationId] = charge.locationName;
        }
      });
      return lookup;
    },
    employeeSummary() {
      const empMap = new Map();

      this.breakdownData.forEach((charge) => {
        if (!charge.recipients) return;

        charge.recipients.forEach((recipient) => {
          const key = recipient.staffId || recipient.name;
          if (!empMap.has(key)) {
            empMap.set(key, {
              staffId: recipient.staffId,
              paycomCode: recipient.staffId,
              name: recipient.name,
              email: recipient.email,
              phone: recipient.phone,
              totalAmount: 0,
              shareCount: 0,
              uniqueTips: new Set(),
              locations: new Set(),
              tips: [],
            });
          }

          const emp = empMap.get(key);
          emp.totalAmount += recipient.shareAmount || 0;
          emp.shareCount += 1;
          emp.uniqueTips.add(charge.tipId);
          emp.locations.add(charge.locationName);
          emp.tips.push({
            ...recipient,
            tipId: charge.tipId,
            tipAmount: charge.tipAmount,
            tipAmountFormatted: charge.tipAmountFormatted,
            tipDateFormatted: charge.tipDateFormatted,
            locationName: charge.locationName,
          });
        });
      });

      return Array.from(empMap.values()).map((emp) => ({
        ...emp,
        locations: Array.from(emp.locations),
        uniqueTipCount: emp.uniqueTips.size,
      }));
    },
  },

  async created() {
    await this.fetchData();
  },

  methods: {
    async fetchData() {
      this.isLoading = true;
      this.loadingStatus = "Fetching report details...";
      this.error = null;

      try {
        const reportResponse = await this.$store.dispatch("callAPI", {
          action: "getSplitReport",
          reportId: this.reportId,
        });
        this.report = reportResponse.data.data;

        if (this.report.status !== "complete") {
          this.error = "This report is still processing. Please wait.";
          return;
        }

        const breakdownUrl = this.report.downloads?.json?.breakdown;

        if (!breakdownUrl) {
          this.isLegacyReport = true;
          return;
        }

        this.loadingStatus = "Downloading tip breakdown data...";
        const [breakdownResp, timesheetsResp] = await Promise.all([
          fetch(breakdownUrl),
          this.report.downloads?.json?.timesheets
            ? fetch(this.report.downloads.json.timesheets)
            : Promise.resolve(null),
        ]);

        if (breakdownResp.ok) {
          this.breakdownData = await breakdownResp.json();
        }
        if (timesheetsResp?.ok) {
          this.timesheetsData = await timesheetsResp.json();
        }

        this.buildLookupMaps();
      } catch (e) {
        console.error("Error fetching data:", e);
        this.error = "Failed to load data. Please try again.";
      } finally {
        this.isLoading = false;
        this.loadingStatus = "";
      }
    },

    buildLookupMaps() {
      this.locationMap = new Map();

      this.breakdownData.forEach((charge) => {
        if (charge.locationId && !this.locationMap.has(charge.locationId)) {
          this.locationMap.set(charge.locationId, {
            name: charge.locationName,
          });
        }
      });
    },

    openEmployeeModal(employee) {
      this.employeeModal.employee = employee;
      this.employeeModal.show = true;
    },

    openChargeModal(charge) {
      this.chargeModal.charge = charge;
      this.chargeModal.show = true;
    },

    viewTipSplit(tipId) {
      const charge = this.breakdownData.find((c) => c.tipId === tipId);
      if (charge) {
        this.chargeModal.charge = charge;
        this.chargeModal.show = true;
      }
    },

    async downloadPayrollFiles() {
      if (this.isDownloading || !this.report?.downloads?.payroll) return;

      this.isDownloading = true;
      try {
        const payrollUrls = Object.values(this.report.downloads.payroll);
        for (const url of payrollUrls) {
          window.open(url);
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      } catch (e) {
        console.error("Error downloading payroll files:", e);
      } finally {
        this.isDownloading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.card-tabs {
  border-bottom: none;
  margin-bottom: 0;
  background: transparent;
  position: relative;

  .nav-item {
    margin-bottom: 0;
  }

  .nav-link {
    border: 1px solid transparent;
    border-bottom: none;
    border-top-left-radius: 0.375rem;
    border-top-right-radius: 0.375rem;
    padding: 0.75rem 1.5rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: #8898aa;
    background: transparent !important;
    background-image: none !important;
    transition: all 0.15s ease;
    position: relative;

    &:hover {
      color: #525f7f;
      border-color: #e9ecef #e9ecef transparent;
      background: rgba(255, 255, 255, 0.5) !important;
    }

    &.active {
      color: #32325d;
      background: #fff !important;
      background-color: #fff !important;
      background-image: none !important;
      border-color: #e9ecef #e9ecef transparent;
      z-index: 1;
    }
  }
}

.card-tabs + div > .card {
  border-top-left-radius: 0 !important;
  border-top-right-radius: 0 !important;
  margin-top: 0;
  box-shadow: 0 0 2rem 0 rgba(136, 152, 170, 0.15) !important;
}
</style>
